import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Button from "../ButtonUI";
import { useAtom } from "jotai";
import { userAtom } from "../../state/auth";
import { useDropzone } from "react-dropzone";
import { ReactComponent as Close } from "../../assets/icons/icon-close.svg";
import makeToast from "../Snackbar";
import { addPhotos, analyzeImage } from "../../api/medecin";
import { refreshAtom } from "../../state/refreshPatientInfo";
import { refreshAtomRequest } from "../../state/refreshRequestList";
import Compressor from "compressorjs";
import Input from "../Input";
import HumanCard from "../HumanCard";
import Carousel from "../Carousel";
import { QualityAnalysis } from "./QualityImage";
type QualityResult = {
  blur_variance: number;
  brightness_mean: number;
  brightness_status: string;
  contrast_std: number;
  filename: string;
  is_blurry: boolean;
  low_contrast: boolean;
  overall_quality: string;
};

interface Entry {
  selectedPart: string | null;
  files: any[];
  message: string;
  anciennete: string;
  taille: string;
  douleur: "oui" | "non" | "inconnue";

}

const UploadDemandeImages: React.FC<any> = ({ setDemandeInfo, demandeId }) => {
  const [loading, setLoading] = useState(false);
  const [user] = useAtom(userAtom);
  const [refresh, setRefresh] = useAtom(refreshAtom);
  const [progress, setProgress] = useState(0);
  const [uploadToast, setUploadToast] = useState(false);
  const [refreshRequestList, setRefreshRequestList] =
    useAtom(refreshAtomRequest);

  const [entries, setEntries] = useState<Entry[]>([
    { selectedPart: null, files: [], message: "", anciennete: "", taille: "", douleur: "inconnue" },
  ]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [confirm, setConfirm] = useState(false);
  const [withPhotosLater, setWithPhotosLater] = useState(false);
  const [qualityData, setQualityData] = useState<QualityResult | null>(null);

  const currentEntry = entries[currentIndex];

  const removeLastPhoto = () => {
    setEntries((prev) => {
      const updated = [...prev];
      const entry = updated[currentIndex];
      if (!entry) return prev;

      entry.files = entry.files.slice(0, -1);

      if (entry.files.length === 0) {
        entry.selectedPart = null;
        entry.message = "";
        entry.anciennete = "";
        entry.taille = "";
        entry.douleur = "inconnue";
        setConfirm(false);
      }

      updated[currentIndex] = entry;
      return updated;
    });

    setQualityData(null);
  };

  const handleDeleteImage = (entryIndex: number, imageIndex: number) => {
    setEntries((prev) => {
      const updated = [...prev];

      const entry = updated[entryIndex];
      if (!entry) return prev;

      updated[entryIndex] = {
        ...entry,
        files: entry.files.filter((_, idx) => idx !== imageIndex),
      };

      return updated;
    });
  };

  const handleDeleteEntry = (entryIndex: number) => {
    setEntries((prev) => {
      const updated = prev.filter((_, idx) => idx !== entryIndex);

      if (updated.length === 0) {
        setConfirm(false);
        setCurrentIndex(0);

        return [
          {
            selectedPart: null,
            files: [],
            message: "",
            anciennete: "",
            douleur: "inconnue",
            taille: "",
          },
        ];
      }

      return updated;
    });

    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/png, image/jpeg, image/jpg, image/webp" as any,
    onDrop: (acceptedFiles) => {
      acceptedFiles.forEach((file) => {
        new Compressor(file, {
          quality: 0.8,
          maxWidth: 2048,
          maxHeight: 2048,
          success: async (blob) => {
            const compressedFile = new File([blob], file.name, { type: blob.type });

            setEntries((prev) => {
              const updated = [...prev];
              updated[currentIndex] = {
                ...updated[currentIndex],
                files: [...updated[currentIndex].files, compressedFile],
              };
              return updated;
            });

            try {
              const formData = new FormData();
              formData.append("image", compressedFile);
              const res = await analyzeImage(formData);
              const result = res.data.data.results[0];
              setQualityData(result);

              if (result.overall_quality !== "accept") {
                makeToast("error", "La photo est de mauvaise qualité. Veuillez reprendre la photo.");
              }
            } catch (err) {
              console.error("Error analyzing image:", err);
            }
          },
        });
      });
    },
  });

  useEffect(() => {
    return () => {
      entries.forEach((entry) =>
        entry.files.forEach((file) => URL.revokeObjectURL(file.preview)),
      );
    };
  }, [entries]);

  const formik = useFormik({
    initialValues: {
      demandeId: demandeId,
      doctor_id: user?._id,
      withPhotos: false,
    },
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setUploadToast(true);

        const payload = {
          ...values,
          entries: entries.filter((e) => e.files.length > 0),
        };
        console.log(payload);
        const response = await addPhotos(
          payload,
          (event: { loaded: number; total: number }) => {
            setProgress(Math.round((100 * event.loaded) / event.total));
          },
        );
        console.log(response);
        if (response.status === "Success") {
          setDemandeInfo(response.data.updatedDemande);
          setRefresh({ state: !refresh?.state });
          setRefreshRequestList({ state: !refreshRequestList?.state });
          makeToast("success", response.message);
        } else {
          makeToast("error", response.message);
        }
      } catch (error) {
        makeToast("error", "Erreur lors de l'envoi");
      } finally {
        setLoading(false);
        setUploadToast(false);
      }
    },
  });

  const removeFile = (filePreview: string) => {
    setEntries((prev) => {
      const updated = [...prev];
      updated[currentIndex].files = updated[currentIndex].files.filter(
        (f) => f.preview !== filePreview,
      );
      return updated;
    });
  };

  return !uploadToast ? (
    <form
      className="w-full flex flex-col items-center mb-8 mt-4 space-y-4"
      onSubmit={formik.handleSubmit}
    >
      <div className="flex flex-col items-center space-y-6 p-4 w-full max-w-md">
        {/* Step 1: Human Body Selection */}
        {!confirm && (
          <div className="body-step flex flex-col items-center space-y-6 w-full">
            <HumanCard
              selectedPart={currentEntry.selectedPart}
              onSelectPart={(part: string | null) => {
                const newEntries = [...entries];
                newEntries[currentIndex].selectedPart = part;
                setEntries(newEntries);
              }}
            />
            {currentEntry.selectedPart && (
              <button
                type="button"
                className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg shadow-md hover:bg-blue-700"
                onClick={() => setConfirm(true)}
              >
                ✔ Confirmer la zone
              </button>
            )}
          </div>
        )}

        {/* Step 2: Upload Zone */}
        {confirm && (
          <section className="flex flex-col items-center w-full space-y-4">
            <div
              {...getRootProps({
                className:
                  "dropzone flexCenter w-full border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-500 cursor-pointer",
              })}
            >
              <input {...getInputProps()} />
              <p className="text-center text-gray-500">
                Glissez les images pour :{" "}
                <strong>{currentEntry.selectedPart}</strong>
              </p>
            </div>

            {currentEntry.files.length > 0 && (
              <aside className="thumbsContainer flex flex-wrap gap-4 mt-4 w-full">
                {currentEntry.files.map((file, index) => (
                  <div
                    key={index}
                    className="w-24 h-24 border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center"
                  >
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`preview-${index}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </aside>
            )}


            {currentEntry.files.length > 0 && (
              <>
              {/* Ancienneté de la lésion */}
                <label className="mt-4 ">
                  Ancienneté de la lésion <span className="text-red-600">(*)</span>
                </label>
                <input
                  type="text"
                  placeholder=""
                  value={entries[currentIndex].anciennete}
                  onChange={(e) => {
                    const newEntries = [...entries];
                    newEntries[currentIndex].anciennete = e.target.value;
                    setEntries(newEntries);
                  }}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 mt-4"
                />

                {/* Taille */}
                <label className="mt-4">
                  Taille (mm) <span className="text-red-600">(*)</span>
                </label>
                <input
                  type="number"
                  placeholder=""
                  value={entries[currentIndex].taille}
                  onChange={(e) => {
                    const newEntries = [...entries];
                    newEntries[currentIndex].taille = e.target.value;
                    setEntries(newEntries);
                  }}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                />

                {/* Douleur */}
                <label className="mt-4">
                  Douleur <span className="text-red-600">(*)</span>
                </label>
                <div className="flex gap-4">
                  {["oui", "non", "inconnue"].map((val) => (
                    <label key={val} className="flex items-center gap-1 mt-3">
                      <input
                        type="radio"
                        name={`douleur-${currentIndex}`}
                        value={val}
                        checked={entries[currentIndex].douleur === val}
                        onChange={() => {
                          const newEntries = [...entries];
                          newEntries[currentIndex].douleur = val as "oui" | "non" | "inconnue";
                          setEntries(newEntries);
                        }}
                        required
                      />
                      {val}
                    </label>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Message..."
                  value={currentEntry.message}
                  onChange={(e) => {
                    const newEntries = [...entries];
                    newEntries[currentIndex].message = e.target.value;
                    setEntries(newEntries);
                  }}
                  className="w-full px-4 py-2 border rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => {
                    setEntries([
                      ...entries,
                      {
                        selectedPart: null,
                        files: [],
                        message: "",
                        anciennete: "",
                        taille: "",
                        douleur: "inconnue",
                      },
                    ]);
                    setCurrentIndex(entries.length);
                    setConfirm(false);
                  }}
                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg shadow-md hover:bg-blue-700 transition-colors duration-300 mt-4"
                >
                  + Ajouter une autre lésion
                </button>
              </>
            )}

            {currentEntry.files.length > 0 && qualityData && (
              <div className="flex flex-col items-center w-full space-y-2 mt-4">
                <QualityAnalysis data={qualityData} />
                {qualityData?.overall_quality !== "accept" && (
                  <button
                    type="button"
                    onClick={removeLastPhoto}
                    className="mt-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                  >
                    Reprendre la photo
                  </button>
                )}
              </div>
            )}
          </section>
        )}

        {/* Carousel of all entries */}
        {entries.some((e) => e.files.length > 0) && (
          <Carousel
            entries={entries.filter((e) => e.files.length > 0)}
            onDeleteImage={handleDeleteImage}
            onDeleteEntry={handleDeleteEntry}
          />
        )}
      </div>

      {/* Footer Actions */}
      <div className="w-full max-w-md space-y-4">
        <Button
          status="primary"
          type="submit"
          className="w-full h-12"
          disabled={
            loading || (!withPhotosLater && entries[0].files.length === 0)
          }
        >
          {loading ? "Envoi..." : "Envoyer la demande"}
        </Button>
      </div>
    </form>
  ) : (
    <div className="w-full px-6 mt-10">
      <div className="font-bold text-gray-700 mb-2">
        Téléchargement : {progress}%
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-blue-600 h-2 rounded-full transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

export default UploadDemandeImages;
